import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { CategoryValue, toCategoryParam } from './category-filter';

export type ManagedFile = RpxResponse<typeof rpx.mediaManager.getManagedFiles>['items'][number];
export type DuplicateMatch = NonNullable<
  RpxResponse<typeof rpx.mediaManager.findDuplicateFiles>['match']
>;
export type FolderListing = RpxResponse<typeof rpx.mediaManager.listFolders>;
export type ManagedFolder = FolderListing['folders'][number];
export type ManagedTag = Omit<
  RpxResponse<typeof rpx.mediaManager.listTagSummaries>[number],
  'fileCount'
>;

export type MediaManagerMode = 'manage' | 'pick';

export type View = 'grid' | 'list';
export type SortBy = 'recent' | 'created';
/**
 * undefined = no folder filter (all files); 'root' = files with no folder;
 * 'archived' = archived files (any folder); UUID = that folder.
 */
export type FolderFilter = string | 'root' | 'archived' | undefined;

const CATEGORIES: readonly CategoryValue[] = ['all', 'image', 'video', 'audio', 'document'];
const VIEWS: readonly View[] = ['grid', 'list'];
const SORTS: readonly SortBy[] = ['recent', 'created'];

export type UrlFilters = {
  category: CategoryValue;
  search: string;
  courseId: string | undefined;
  folderId: FolderFilter;
  tagId: string | undefined;
  sortBy: SortBy;
  view: View;
};

export const DEFAULT_FILTERS: UrlFilters = {
  category: 'all',
  search: '',
  courseId: undefined,
  folderId: undefined,
  tagId: undefined,
  sortBy: 'recent',
  view: 'grid',
};

export function readUrlFilters(search: string, fallback: UrlFilters = DEFAULT_FILTERS): UrlFilters {
  const params = new URLSearchParams(search);
  const category = params.get('category') as CategoryValue | null;
  const view = params.get('view') as View | null;
  const sortBy = params.get('sort') as SortBy | null;
  const folderParam = params.get('folder');
  return {
    category: category && CATEGORIES.includes(category) ? category : fallback.category,
    search: params.get('q') ?? fallback.search,
    courseId: params.get('course') || fallback.courseId,
    folderId: folderParam ?? fallback.folderId,
    tagId: params.get('tag') || fallback.tagId,
    sortBy: sortBy && SORTS.includes(sortBy) ? sortBy : fallback.sortBy,
    view: view && VIEWS.includes(view) ? view : fallback.view,
  };
}

export function buildFiltersQuery(f: UrlFilters): string {
  const params = new URLSearchParams();
  if (f.category !== DEFAULT_FILTERS.category) {
    params.set('category', f.category);
  }
  if (f.search) {
    params.set('q', f.search);
  }
  if (f.courseId) {
    params.set('course', f.courseId);
  }
  if (f.folderId) {
    params.set('folder', f.folderId);
  }
  if (f.tagId) {
    params.set('tag', f.tagId);
  }
  if (f.sortBy !== DEFAULT_FILTERS.sortBy) {
    params.set('sort', f.sortBy);
  }
  if (f.view !== DEFAULT_FILTERS.view) {
    params.set('view', f.view);
  }
  const s = params.toString();
  return s ? `${location.pathname}?${s}` : location.pathname;
}

export type State = {
  category: CategoryValue;
  search: string;
  courseId: string | undefined;
  folderId: FolderFilter;
  tagId: string | undefined;
  sortBy: SortBy;
  items: ManagedFile[];
  cursor: string | undefined;
  hasMore: boolean;
  isLoading: boolean;
  hasError: boolean;
};

export const PAGE_SIZE = 20;

export async function fetchPage(opts: {
  category: CategoryValue;
  search: string;
  courseId?: string;
  folderId?: FolderFilter;
  tagId?: string;
  sortBy: SortBy;
  cursor?: string;
  mimePrefixes?: string[];
}): Promise<{ items: ManagedFile[]; cursor: string | undefined; hasMore: boolean }> {
  // 'archived' is a virtual folder: show archived files across all folders.
  // Any other value maps straight to the folder filter.
  const archived = opts.folderId === 'archived';
  return rpx.mediaManager.getManagedFiles({
    archived,
    category: toCategoryParam(opts.category),
    mimePrefixes: opts.mimePrefixes,
    search: opts.search || undefined,
    courseId: opts.courseId,
    folderId: archived ? undefined : opts.folderId,
    tagId: opts.tagId,
    sortBy: opts.sortBy,
    cursor: opts.cursor,
    limit: PAGE_SIZE,
  });
}

export function emptyState(initial?: Partial<State>): State {
  return {
    category: 'all',
    search: '',
    courseId: undefined,
    folderId: undefined,
    tagId: undefined,
    sortBy: 'recent',
    items: [],
    cursor: undefined,
    hasMore: false,
    isLoading: true,
    hasError: false,
    ...initial,
  };
}
